import { Grid, Paper, Stack } from "@mui/material";
import { Button } from "components/ButtonNew";
import { TextFieldInput } from "components/Input";
import { LDSToast } from "components/LDSToast";
import { useTranslation } from "react-i18next";
import { hint } from "utils/textSymbols";

import { ADDRESS_FORM_FIELDS } from "./constants";
import { EpcUploadModal } from "./epc-modal/EpcUploadModal";
import { useAddressStep } from "./hooks/useAddressStep";
import { useEpcUpload } from "./hooks/useEpcUpload";
import { InteractiveLocationMap } from "./InteractiveLocationMap";

export const AddressStep = () => {
  const { t } = useTranslation();

  const {
    addressInfo,
    fetchAddressFromLocation,
    fetchLocationFromAddress,
    handleInputChange,
    handleMapMove,
    handlePickAddressSuggestion,
    isEpcExtractionAvailable,
    isFetchingAddress,
    isFetchingLocation,
    isProjectLocked,
    mapCenter,
    recenterRequestCounter,
    zoomLevel,
  } = useAddressStep();

  const epcUpload = useEpcUpload({ isEnabled: isEpcExtractionAvailable, preserveAddress: isProjectLocked });

  return (
    <>
      <EpcUploadModal {...epcUpload} />
      <Grid item xs={6}>
        <InteractiveLocationMap
          addressInfo={addressInfo}
          center={mapCenter}
          className=""
          fetchAddressLabel={t("AddBuilding.addressStep.fetchAddressButton") as string}
          isFetchingAddress={isFetchingAddress}
          isReadOnly={isProjectLocked}
          onFetchAddress={isProjectLocked ? undefined : fetchAddressFromLocation}
          onMapMove={handleMapMove}
          onPickAddressSuggestion={handlePickAddressSuggestion}
          recenterRequest={recenterRequestCounter}
          zoomLevel={zoomLevel}
        />
      </Grid>
      <Grid item xs={6}>
        <Paper className="p-10">
          <Stack className="h-full flex-col justify-between">
            <div>
              <Stack className="items-start justify-between gap-4">
                <h1 className="font-ds-headline-xl">{t("AddBuilding.addressStep.title")}</h1>
                {isEpcExtractionAvailable && (
                  <Button onClick={epcUpload.handlers.openModal} variant="secondary">
                    {epcUpload.states.hasAppliedEpc
                      ? t("AddBuilding.epcModal.reuploadButton")
                      : t("AddBuilding.epcModal.openButton")}
                  </Button>
                )}
              </Stack>
              <Grid className="mt-7" container spacing={6}>
                {ADDRESS_FORM_FIELDS.map(({ i18nKey, key, required, xs }) => (
                  <Grid item key={key} xs={xs}>
                    <TextFieldInput
                      className="w-full"
                      disabled={isProjectLocked}
                      height="4.0rem"
                      label={t(`AddBuilding.addressStep.fields.${i18nKey}.label`)}
                      onChange={handleInputChange(key)}
                      placeholder={t(`AddBuilding.addressStep.fields.${i18nKey}.placeholder`) ?? ""}
                      required={required}
                      rootClassName=""
                      type="text"
                      value={addressInfo?.[key]}
                    />
                  </Grid>
                ))}
                {!isProjectLocked && (
                  <Grid className="flex items-end" item key="fetch-location" xs={6}>
                    <Button
                      className="w-full"
                      height="4.0rem"
                      isLoading={isFetchingLocation}
                      onClick={fetchLocationFromAddress}
                      variant="secondary"
                    >
                      {t("AddBuilding.addressStep.fetchLocationButton")}
                    </Button>
                  </Grid>
                )}
              </Grid>
            </div>
            <LDSToast
              className="mt-7 w-full"
              invisibleOnDismiss
              message={`${hint} ${t("AddBuilding.addressStep.infoBannerMessage")}`}
            />
          </Stack>
        </Paper>
      </Grid>
    </>
  );
};
